import { isMigratedSessionNotes } from "./migrated-session";

/**
 * PDV stopa kojom su računati redovi nastali PRIJE migracije koja je dodala snapshot
 * kolone. Namjerno hardkodovana i namjerno NE čita `getMoneyConfig`: ovo je jedina
 * dokumentovana iznimka od pravila "nema stope van money-config.ts" (vidi CLAUDE.md,
 * sekcija "Novac, PDV i provizija"). Trenutna stopa bi ovdje bila pogrešna, jer bi
 * promjena stope u postavkama tiho pomjerila prikaz historijskih redova koji su
 * naplaćeni po staroj. Redovi sa snapshotom nikad ne dolaze do ove konstante.
 */
const LEGACY_VAT_RATE_BEFORE_SNAPSHOT = 0.17;

export interface AdminPaymentBreakdown {
  clientPaid: number | null;
  netBeforeVat: number | null;
  vat: number | null;
  platformFee: number | null;
  /** Ulazni PDV sa terapeutove fakture, sadržan u `therapistEarnings`. null na
   *  redovima bez snapshota (prije migracije nije ni postojao). */
  therapistVat: number | null;
  therapistEarnings: number | null;
}

export function computeAdminPaymentBreakdown(input: {
  priceCharged?: number | null;
  price?: number | null;
  therapistEarnings?: number | null;
  vatAmount?: number | null;
  platformBase?: number | null;
  platformFee?: number | null;
  therapistVat?: number | null;
  /** Bilješka seanse; prepoznaje WP-migrirane redove (`src/lib/migrated-session.ts`). */
  notes?: string | null;
}): AdminPaymentBreakdown {
  const clientPaid = normalize(input.priceCharged ?? input.price ?? null);
  const therapistEarnings = normalize(input.therapistEarnings ?? null);
  const therapistVat = normalize(input.therapistVat ?? null);

  if (clientPaid === null) {
    return {
      clientPaid: null,
      netBeforeVat: null,
      vat: null,
      platformFee: null,
      therapistVat,
      therapistEarnings,
    };
  }

  // WP-migriran red bez snapshota (već isplaćen prije popravke migracije, pa ga
  // scripts/repair-migrated-session-money.ts ne dira): `priceCharged` je WooCommerce
  // iznos BEZ PDV-a, a da li je WP na njega naplatio PDV iz reda se ne vidi. Zato je
  // neto upravo `priceCharged`, a PDV i ono što je klijent platio su nepoznati, umjesto
  // da se iz neta izvuče izmišljen PDV kao iz bruta.
  if (normalize(input.platformBase ?? null) === null && isMigratedSessionNotes(input.notes)) {
    return {
      clientPaid: null,
      netBeforeVat: clientPaid,
      vat: null,
      platformFee: therapistEarnings === null ? null : round2(clientPaid - therapistEarnings),
      therapistVat,
      therapistEarnings,
    };
  }

  // Snimljen snapshot je mjerodavan; preračun je samo za historijske redove (prije
  // migracije koja je dodala kolone) gdje snapshot ne postoji.
  const netBeforeVat =
    normalize(input.platformBase ?? null) ??
    round2(clientPaid / (1 + LEGACY_VAT_RATE_BEFORE_SNAPSHOT));
  const vat = normalize(input.vatAmount ?? null) ?? round2(clientPaid - netBeforeVat);
  const platformFee =
    normalize(input.platformFee ?? null) ??
    (therapistEarnings === null ? null : round2(netBeforeVat - therapistEarnings));

  return {
    clientPaid,
    netBeforeVat,
    vat,
    platformFee,
    therapistVat,
    therapistEarnings,
  };
}

export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return "\u2014";
  return value.toFixed(2);
}

function normalize(value: number | null): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return round2(value);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
